import {
	ApiError,
	AppError,
	AppMimeType,
	ErrorCode,
	Language,
} from '@/types/types';
import { formatDistance, Locale } from 'date-fns';
import { flatten } from 'lodash';
import { isAxiosError } from 'axios';
import { User, UserAccessLevel } from '@/types/user';
import { MontageType } from '@/api/ProductTypeApi';
import { MIME_TYPES } from '@mantine/dropzone';
import { Investment, InvestmentStatus } from '@/api/InvestmentApi';
import { CompanyTransportType, Currency } from '@/api/CompanyApi';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';

export function formatDateForApi(date: moment.MomentInput) {
	return moment(date).format('YYYY-MM-DD');
}

export function formatDateForPresentation(date: moment.MomentInput) {
	if (!date) return '-';
	return moment(date).format('DD.MM.YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput | string = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

const PHONE_REGEX =
	/^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/;

/**
 *
 * @param phoneNumber validate international
 * @returns
 */
export function validatePhoneNumber(phoneNumber: string) {
	return PHONE_REGEX.test(phoneNumber) && phoneNumber.length >= 9;
}

/**
 *
 * @param accountNumber validate polish
 * @returns
 */
export function validateAccountNumber(accountNumber: string) {
	const trimmed = accountNumber.trim();
	return /\d{26}/.test(trimmed);
}

/**
 *
 * @param postalCode validate polish
 * @returns
 */
export function validatePostalCode(postalCode: string) {
	const trimmed = postalCode.trim();
	return /^[0-9]{2}-[0-9]{3}/.test(trimmed);
}

export function removeWhiteSpaces(string: string) {
	return string.replace(/\s/g, '');
}

export function isValidDateObj(date: moment.MomentInput) {
	return moment(date).isValid();
}

export function sortByDate(
	dateA: string | Date,
	dateB: string | Date,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function stripHTML(string: string) {
	return string.replace(/(<([^>]+)>)/gi, '');
}

export function handleUnload(e: BeforeUnloadEvent) {
	e.preventDefault();
	e.returnValue = '';
}

export const ID_REGEX =
	/[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[89ab][a-f0-9]{3}-?[a-f0-9]{12}/g;

export function getIdsFromText(text: string) {
	return [...new Set(flatten([...text.matchAll(ID_REGEX)]))];
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export function getContent(conent: Record<string, string>, slug: string) {
	return conent[slug];
}

export function formatDateFromNow(date: Date, locale: Locale) {
	return formatDistance(date, new Date(), {
		locale,
		includeSeconds: true,
		addSuffix: true,
	});
}

export const formatPrice = (value = 0, opts?: Intl.NumberFormatOptions) => {
	const lang = opts?.currency === Currency.EUR ? 'en' : 'pl';

	const formatter = new Intl.NumberFormat(lang, {
		style: 'currency',
		currency: Currency.PLN,
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export function checkIfScreenZoomedIn() {
	return window.devicePixelRatio >= 1.2;
}

export const isAppError = (error: unknown): error is AppError => {
	return !!(error as AppError).status && !!(error as AppError).stack;
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function getClosestNumber(number: number, array: number[]) {
	return array.reduce(
		(prev, curr) =>
			Math.abs(curr - number) < Math.abs(prev - number) ? curr : prev,
		0
	);
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function formatFullName(firstName?: string, lastName?: string) {
	if (firstName && lastName) return `${firstName} ${lastName}`;
	if (!firstName && !lastName) return `-`;
	return firstName || lastName;
}

export function getMimeTypeName(type: AppMimeType) {
	switch (type) {
		case MIME_TYPES.pdf:
			return 'PDF';
		case MIME_TYPES.zip:
			return 'ZIP';

		default:
			return 'Nieznany typ pliku';
	}
}

export function mapCompanyTransportTypeName(
	companyTransportType: CompanyTransportType
) {
	switch (companyTransportType) {
		case CompanyTransportType.EXTERIOR:
			return 'Zewnętrzny';
		case CompanyTransportType.INTERIOR:
			return 'Wewnętrzny';
	}
}

export function getInvestmentName(status: InvestmentStatus) {
	switch (status) {
		case InvestmentStatus.NOT_SENT:
			return 'Nie wysłane';
		case InvestmentStatus.SENT:
			return 'Wysłano';
		case InvestmentStatus.CANCELLED:
			return 'Cofnięte';
		case InvestmentStatus.IN_PROGRESS:
			return 'W realizacji';
		case InvestmentStatus.COMPLETED:
			return 'Zakończone';
	}
}

export function getInvestmentStatusBagdeData(status: InvestmentStatus) {
	switch (status) {
		case InvestmentStatus.NOT_SENT:
			return {
				name: getInvestmentName(status),
				color: 'blue',
			};
		case InvestmentStatus.SENT:
			return {
				name: getInvestmentName(status),
				color: 'cyan',
			};
		case InvestmentStatus.CANCELLED:
			return {
				name: getInvestmentName(status),
				color: 'yellow',
			};
		case InvestmentStatus.IN_PROGRESS:
			return {
				name: getInvestmentName(status),
				color: 'pink',
			};
		case InvestmentStatus.COMPLETED:
			return {
				name: getInvestmentName(status),
				color: 'green',
			};
	}
}

export function hasEditInvestmentAccess(investment: Investment, user: User) {
	switch (user.accessLevel) {
		case UserAccessLevel.LEVEL_0:
			return false;
		case UserAccessLevel.LEVEL_1:
			return investment.user.id === user.id;
		case UserAccessLevel.LEVEL_2:
			return true;
	}
}

export function isInvestmentEditable(status: InvestmentStatus) {
	return [InvestmentStatus.NOT_SENT, InvestmentStatus.CANCELLED].includes(
		status
	);
}

export function canCreateInvestment(user: User) {
	switch (user.accessLevel) {
		case UserAccessLevel.LEVEL_0:
			return false;
		case UserAccessLevel.LEVEL_1:
		case UserAccessLevel.LEVEL_2:
			return true;
	}
}

export function canAccessTemplates(user: User) {
	switch (user.accessLevel) {
		case UserAccessLevel.LEVEL_0:
		case UserAccessLevel.LEVEL_1:
			return false;
		case UserAccessLevel.LEVEL_2:
			return true;
	}
}

export function getMontageTypeName(montageType: MontageType) {
	switch (montageType) {
		case MontageType.INSIDE:
			return 'Wnęka okienna';
		case MontageType.ON_WALL:
			return 'Rama okienna';
		case MontageType.BOTH:
			return 'Wnęka okienna i rama okienna';
	}
}
